import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CalendlyButton } from "@/components/Calendly";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-ink-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
          : "bg-white/0 border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-24 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_restore-sleep/artifacts/ote6ytks_Screenshot%202026-05-03%20at%202.56.54%E2%80%AFAM.png"
            alt="Restore.JC"
            className="h-16 w-16 rounded-full object-cover"
          />
          <span className="font-display text-2xl tracking-tight text-ink-900 font-light">
            restore<span className="text-brand-600">.</span>
            <span className="font-medium">jc</span>
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-body text-sm tracking-wide transition-colors ${
                    isActive ? "text-ink-900" : "text-ink-600 hover:text-ink-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden md:block">
          <CalendlyButton variant="pill" testId="navbar-cta-book" withArrow={false}>
            Book Assessment
          </CalendlyButton>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-ink-900"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden bg-white border-t border-ink-200"
        >
          <ul className="px-6 py-6 space-y-4">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="block font-display text-2xl text-ink-900"
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-4">
              <CalendlyButton testId="mobile-cta-book" className="w-full justify-center">
                Book Assessment
              </CalendlyButton>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
